import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Phone, MapPin, Clock, DollarSign, CheckCircle2, X, AlertCircle,
  Calendar, ChevronRight, Briefcase, Trophy, Sparkles,
} from "lucide-react";

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const BG   = "#F8F9FA";
const DARK = "#1A1A2E";
const GREEN = "#1A7A4A";
const RED  = "#A32D2D";
const GREY = "#5a5d96";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const URG = {
  urgent: { label: "URGENT", color: RED, emoji: "🔴" },
  this_week: { label: "THIS WEEK", color: GOLD, emoji: "🟡" },
  planning: { label: "PLANNING", color: GREEN, emoji: "🟢" },
};

const STATUS_OPTIONS = [
  { value: "called_back", label: "CALLED BACK", color: NAVY },
  { value: "quoted", label: "QUOTED", color: GOLD },
  { value: "won_job", label: "WON JOB", color: GREEN, needsValue: true },
  { value: "not_interested", label: "NOT INTERESTED", color: GREY },
];

const LeadsPage = () => {
  const [contractorId, setContractorId] = useState(
    () => localStorage.getItem("nearnow_contractor_id") || ""
  );
  const [leads, setLeads] = useState([]);
  const [counts, setCounts] = useState({});
  const [revenue, setRevenue] = useState(0);
  const [filter, setFilter] = useState("all");  // all | urgent | new | won_job
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [valuePrompt, setValuePrompt] = useState(null); // { lead, status }
  const [summary, setSummary] = useState(null);

  // Count of leads in the last 7 days — drives the Pro upsell banner copy
  const leadsThisWeek = React.useMemo(() => {
    if (!leads?.length) return 0;
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return leads.filter((l) => {
      const t = l.created_at ? Date.parse(l.created_at) : 0;
      return t >= cutoff;
    }).length;
  }, [leads]);

  // Pro+ plans skip the upsell banner
  const isPaidPro = ["pro", "elite", "market_leader"].includes(
    String(summary?.plan || "").toLowerCase()
  );
  const showProUpsell = !!contractorId && !!summary && !isPaidPro;

  const load = useCallback(async () => {
    if (!contractorId) return;
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`${API}/aria/leads`, { params: { contractor_id: contractorId } });
      setLeads(r.data.leads || []);
      setCounts(r.data.counts || {});
      setRevenue(r.data.revenue_cents || 0);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(detail || e?.message || "Could not load leads.");
    } finally {
      setLoading(false);
    }
    // Fetch contractor summary in parallel so we know plan for the upsell
    try {
      const s = await axios.get(`${API}/contractor/${contractorId}/summary`);
      setSummary(s.data);
    } catch {
      setSummary(null);
    }
  }, [contractorId]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (lead, status) => {
    const opt = STATUS_OPTIONS.find((o) => o.value === status);
    if (opt?.needsValue) { setValuePrompt({ lead, status }); return; }
    await axios.patch(`${API}/aria/leads/${lead.id}`, { status });
    await load();
  };

  const submitValue = async (cents) => {
    if (!valuePrompt) return;
    await axios.patch(`${API}/aria/leads/${valuePrompt.lead.id}`, {
      status: valuePrompt.status, job_value_cents: cents,
    });
    setValuePrompt(null);
    await load();
  };

  const saveId = (id) => {
    const v = (id || "").trim();
    if (v) localStorage.setItem("nearnow_contractor_id", v);
    setContractorId(v);
  };

  const filtered = leads.filter((l) => {
    if (filter === "all") return true;
    if (filter === "urgent") return l.urgency === "urgent";
    if (filter === "new") return l.status === "new";
    if (filter === "won_job") return l.status === "won_job";
    return true;
  });

  return (
    <div data-testid="leads-page" style={{ background: BG, minHeight: "100vh" }}>
      {/* Hero */}
      <section style={{
        background: `linear-gradient(180deg, ${NAVY} 0%, #060d1a 100%)`,
        color: "#fff", padding: "28px 16px 22px", borderBottom: `3px solid ${GOLD}`,
      }}>
        <div style={{ maxWidth: 980, margin: "0 auto" }}>
          <p style={{ color: GOLD, fontFamily: "Bebas Neue, sans-serif", letterSpacing: 4, margin: 0, fontSize: 12 }}>
            ARIA · YOUR AI RECEPTIONIST
          </p>
          <h1 style={{ fontFamily: "Bebas Neue, sans-serif",
                       fontSize: "clamp(32px, 8vw, 56px)", letterSpacing: 1,
                       margin: "4px 0 4px", lineHeight: 1.05 }}>
            Your Leads
          </h1>
          <p style={{ color: "#cbd0db", fontSize: 14, margin: 0 }}>
            Every missed call. Every captured detail. Every dollar earned.
          </p>
        </div>
      </section>

      {/* Identity bar */}
      <section style={{ maxWidth: 980, margin: "0 auto", padding: "18px 16px 0" }}>
        <div data-testid="leads-id-bar" style={{
          background: "#fff", borderRadius: 12, padding: 14,
          border: `1px solid ${GOLD}33`, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap",
        }}>
          <Briefcase color={NAVY} size={20} />
          <label className="nn-label" style={{ color: NAVY, fontSize: 13 }}>CONTRACTOR ID</label>
          <input
            data-testid="leads-contractor-id"
            className="nn-input"
            style={{ flex: 1, minWidth: 200, minHeight: 48 }}
            placeholder="paste your contractor ID"
            value={contractorId}
            onChange={(e) => setContractorId(e.target.value)}
            onBlur={(e) => saveId(e.target.value)}
          />
          <button data-testid="leads-reload" onClick={load}
            disabled={!contractorId} className="btn-gold tap" style={{ minHeight: 48 }}>
            LOAD
          </button>
        </div>
      </section>

      {/* Pro Plan Upsell — only when contractor is on Starter / Growth */}
      {showProUpsell && (
        <ProUpsellBanner
          leadsThisWeek={leadsThisWeek}
          contractorId={contractorId}
          currentPlan={summary?.plan}
        />
      )}

      {/* Refer & Earn — always shown to active contractors */}
      {contractorId && summary && (
        <ReferAndEarnWidget contractorId={contractorId} />
      )}

      {/* Stats + filters */}
      {contractorId && !error?.error && (
        <section style={{ maxWidth: 980, margin: "16px auto 0", padding: "0 16px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
            <Stat label="Total" value={leads.length} testid="stat-total" />
            <Stat label="New" value={counts.new || 0} testid="stat-new" color={GOLD} />
            <Stat label="Won Jobs" value={counts.won_job || 0} testid="stat-won" color={GREEN} icon={<Trophy size={16} />} />
            <Stat label="Revenue" value={`$${Math.round(revenue/100).toLocaleString()}`} testid="stat-revenue" color={GOLD} icon={<DollarSign size={16} />} />
          </div>

          <div data-testid="leads-filters" style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 8 }}>
            {[
              ["all", "All", null],
              ["urgent", "🔴 Urgent", RED],
              ["new", "New", GOLD],
              ["won_job", "🏆 Won", GREEN],
            ].map(([k, label, color]) => (
              <button key={k} data-testid={`leads-filter-${k}`}
                onClick={() => setFilter(k)} className="tap"
                style={{
                  background: filter === k ? NAVY : "#fff",
                  color: filter === k ? "#fff" : NAVY,
                  border: `1px solid ${color || "#e5e7ee"}`,
                  borderRadius: 999, padding: "8px 16px", minHeight: 40,
                  fontFamily: "Bebas Neue, sans-serif", letterSpacing: 1.5, fontSize: 12,
                  cursor: "pointer",
                }}>
                {label}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Error */}
      {error && contractorId && (
        <section style={{ maxWidth: 980, margin: "16px auto 0", padding: "0 16px" }}>
          <div data-testid="leads-error" style={{
            background: "#fff", border: `1px solid ${RED}66`, borderLeft: `4px solid ${RED}`,
            padding: 14, borderRadius: 10, color: RED,
          }}>
            {typeof error === "string" ? error : "Could not load leads."}
          </div>
        </section>
      )}

      {/* Lead list */}
      {contractorId && !error && (
        <section style={{ maxWidth: 980, margin: "18px auto 60px", padding: "0 16px" }}>
          {loading && <p data-testid="loading" style={{ color: GREY, textAlign: "center" }}>Loading…</p>}
          {!loading && filtered.length === 0 && (
            <div data-testid="leads-empty" style={{
              background: "#fff", border: `2px dashed ${GOLD}55`, padding: 28, borderRadius: 14,
              textAlign: "center", color: GREY,
            }}>
              <Phone color={GOLD} size={36} />
              <p style={{ margin: "10px 0 4px", color: NAVY, fontSize: 18, fontWeight: 600 }}>
                {filter === "all" ? "No leads yet." : `No ${filter.replace("_", " ")} leads.`}
              </p>
              <p style={{ margin: 0, fontSize: 14 }}>
                Your AutoPilot is ON. The next missed call will land here automatically.
              </p>
            </div>
          )}
          <div style={{ display: "grid", gap: 12, marginTop: 14 }}>
            {filtered.map((ld) => (
              <LeadCard key={ld.id} lead={ld} onStatus={setStatus} />
            ))}
          </div>
        </section>
      )}

      <AnimatePresence>
        {valuePrompt && (
          <ValueModal
            onClose={() => setValuePrompt(null)}
            onSubmit={submitValue}
            lead={valuePrompt.lead}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const Stat = ({ label, value, testid, color = NAVY, icon }) => (
  <div data-testid={testid} style={{
    background: "#fff", padding: 14, borderRadius: 12,
    border: `1px solid ${GOLD}22`, display: "flex", alignItems: "center", gap: 10,
  }}>
    {icon && <div style={{ color }}>{icon}</div>}
    <div>
      <div style={{ color: GREY, fontSize: 11, letterSpacing: 1, textTransform: "uppercase" }}>{label}</div>
      <div style={{ color, fontFamily: "Bebas Neue, sans-serif", fontSize: 26, letterSpacing: 1, lineHeight: 1 }}>{value}</div>
    </div>
  </div>
);

const LeadCard = ({ lead, onStatus }) => {
  const urg = URG[lead.urgency] || URG.this_week;
  const isWon = lead.status === "won_job";
  return (
    <motion.div
      data-testid={`lead-${lead.id}`}
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      style={{
        background: "#fff", borderRadius: 12, padding: 16,
        borderLeft: `4px solid ${urg.color}`,
        boxShadow: "0 6px 16px rgba(10,22,40,0.06)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <span style={{
            background: `${urg.color}22`, color: urg.color, padding: "3px 8px",
            borderRadius: 4, fontSize: 11, letterSpacing: 1,
            fontFamily: "Bebas Neue, sans-serif", marginRight: 8,
          }}>
            {urg.emoji} {urg.label}
          </span>
          {lead.status !== "new" && (
            <span style={{
              background: isWon ? `${GREEN}22` : `${GREY}22`,
              color: isWon ? GREEN : GREY,
              padding: "3px 8px", borderRadius: 4, fontSize: 11, letterSpacing: 1,
              fontFamily: "Bebas Neue, sans-serif",
            }}>
              {lead.status.replace("_", " ").toUpperCase()}
            </span>
          )}
          <h3 style={{ margin: "10px 0 4px", color: NAVY, fontSize: 18 }}>
            {lead.caller_name}
          </h3>
          <a href={`tel:${lead.caller_phone}`}
            data-testid={`call-${lead.id}`}
            style={{ color: GREEN, fontSize: 16, fontWeight: 600, textDecoration: "none",
                     display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Phone size={16} /> {lead.caller_phone}
          </a>
          <div style={{ marginTop: 8, display: "grid", gap: 4, color: GREY, fontSize: 14 }}>
            {lead.property_address && <Row icon={<MapPin size={14} />} text={lead.property_address} />}
            {lead.callback_scheduled && <Row icon={<Clock size={14} />} text={`Callback: ${lead.callback_scheduled}`} />}
            {isWon && lead.job_value_cents > 0 && (
              <Row icon={<DollarSign size={14} />}
                text={`Won: $${Math.round(lead.job_value_cents/100).toLocaleString()}`}
                bold color={GOLD} />
            )}
          </div>
          <p style={{ marginTop: 10, color: DARK, fontSize: 14, lineHeight: 1.5,
                      background: BG, padding: 10, borderRadius: 6 }}>
            {lead.job_description}
          </p>
          {lead.caller_notes && (
            <p style={{ marginTop: 6, color: GREY, fontSize: 13, fontStyle: "italic",
                        borderLeft: `2px solid ${GOLD}`, paddingLeft: 8 }}>
              📝 {lead.caller_notes}
            </p>
          )}
        </div>
        {lead.status !== "won_job" && lead.status !== "not_interested" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 160 }}>
            {STATUS_OPTIONS.map((opt) => (
              <button key={opt.value} data-testid={`status-${opt.value}-${lead.id}`}
                onClick={() => onStatus(lead, opt.value)} className="tap"
                style={{
                  background: opt.value === "won_job" ? opt.color : "transparent",
                  color: opt.value === "won_job" ? "#fff" : opt.color,
                  border: `1px solid ${opt.color}`,
                  borderRadius: 6, padding: "10px 12px", minHeight: 44,
                  fontFamily: "Bebas Neue, sans-serif", letterSpacing: 1.5, fontSize: 12,
                  cursor: "pointer", display: "inline-flex", alignItems: "center",
                  gap: 6, justifyContent: "center",
                }}>
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

const Row = ({ icon, text, bold, color }) => (
  <div style={{ display: "flex", gap: 6, alignItems: "center",
                color: color || GREY, fontWeight: bold ? 700 : 400 }}>
    {icon} <span>{text}</span>
  </div>
);

const ValueModal = ({ onClose, onSubmit, lead }) => {
  const [value, setValue] = useState("");
  return (
    <motion.div
      data-testid="value-modal"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(10,22,40,0.65)",
               display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 100 }}>
      <motion.div
        initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: 14, padding: 22, width: "100%", maxWidth: 420 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontFamily: "Bebas Neue, sans-serif", fontSize: 26, color: NAVY, letterSpacing: 1 }}>
            🏆 JOB WON
          </h3>
          <button onClick={onClose} className="tap"
            style={{ background: "transparent", border: "none", padding: 6, cursor: "pointer" }}>
            <X size={20} color={NAVY} />
          </button>
        </div>
        <p style={{ color: GREY, fontSize: 14, marginTop: 0 }}>
          What was the value of the {lead?.caller_name} job?
        </p>
        <label className="nn-label" style={{ color: NAVY }}>JOB VALUE ($)</label>
        <input data-testid="value-input" className="nn-input" type="number" min="0"
          value={value} placeholder="0" onChange={(e) => setValue(e.target.value)}
          autoFocus style={{ marginTop: 6 }} />
        <button data-testid="value-submit"
          onClick={() => onSubmit(Math.round((parseFloat(value) || 0) * 100))}
          className="btn-gold tap" style={{ width: "100%", minHeight: 52, marginTop: 14, fontSize: 16 }}>
          SAVE — FEEDS YOUR REVENUE TRACKER
        </button>
      </motion.div>
    </motion.div>
  );
};

// =====================================================================
// Pro Plan Upsell Banner
// =====================================================================
// Targeted CTA on the leads page: shows actual leads-this-week count + math,
// drives Starter/Growth contractors into Pro so they unlock NCGE ($150 ads).
const ProUpsellBanner = ({ leadsThisWeek, contractorId, currentPlan }) => {
  const projected = Math.max(24, leadsThisWeek * 3);
  const lead = Math.max(1, leadsThisWeek);
  return (
    <section data-testid="pro-upsell-banner" style={{ maxWidth: 980, margin: "16px auto 0", padding: "0 16px" }}>
      <div
        style={{
          background: "linear-gradient(135deg, #0A1628 0%, #18233e 100%)",
          color: "#fff", borderRadius: 14, padding: "20px 22px",
          border: `2px solid ${GOLD}`,
          boxShadow: "0 18px 38px rgba(10,22,40,0.18)",
          display: "flex", flexWrap: "wrap", gap: 18, alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ flex: "1 1 320px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: GOLD,
                          letterSpacing: 4, fontSize: 12, fontFamily: "Bebas Neue, sans-serif" }}>
            <Sparkles size={14} /> GROWTH ENGINE · PRO UPGRADE
          </div>
          <h2 data-testid="pro-upsell-headline" style={{
            fontFamily: "Bebas Neue, sans-serif", letterSpacing: 1,
            fontSize: "clamp(22px, 5vw, 30px)", margin: "8px 0 4px", lineHeight: 1.1,
          }}>
            Your dashboard shows <span style={{ color: GOLD }}>{lead} lead{lead === 1 ? "" : "s"}</span> this week —
            imagine <span style={{ color: GOLD }}>{projected}</span>.
          </h2>
          <p style={{ color: "#cbd0db", fontSize: 14, margin: "6px 0 0", lineHeight: 1.5 }}>
            One sale more than pays for this small price. You work and let
            NearNow advertise for you! Upgrade to Pro today and let NearNow run
            a <strong style={{ color: "#fff" }}>$150 ad next week</strong>.
          </p>
          <p style={{ color: "#8a93a6", fontSize: 12, marginTop: 6 }}>
            You&apos;re on <strong>{(currentPlan || "Starter").replace(/_/g, " ")}</strong>. Pro
            unlocks ARIA + the NearNow Growth Engine (Facebook + Instagram ads, full creative + targeting done for you).
          </p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 220 }}>
          <Link
            data-testid="pro-upsell-cta"
            to="/pricing"
            className="btn-gold tap"
            style={{ minHeight: 56, fontSize: 16, textAlign: "center",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      letterSpacing: 1, paddingLeft: 22, paddingRight: 22 }}
          >
            UPGRADE TO PRO →
          </Link>
          {contractorId && (
            <Link
              data-testid="pro-upsell-grow-link"
              to={`/contractor/${contractorId}/grow`}
              style={{ color: "#cbd0db", fontSize: 12, textAlign: "center",
                        textDecoration: "underline", paddingTop: 4 }}
            >
              See what NearNow Growth Engine does
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};

export default LeadsPage;

// =====================================================================
// Refer & Earn $50 Widget
// =====================================================================
const ReferAndEarnWidget = ({ contractorId }) => (
  <section data-testid="refer-earn-widget" style={{ maxWidth: 980, margin: "10px auto 0", padding: "0 16px" }}>
    <div style={{
      background: "#fff", border: `1px solid ${GOLD}`, borderRadius: 12,
      padding: "16px 22px", display: "flex", alignItems: "center",
      justifyContent: "space-between", flexWrap: "wrap", gap: 12,
      boxShadow: "0 8px 22px rgba(212,175,55,0.10)",
    }}>
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8,
                       color: "#8a5a00", letterSpacing: 3, fontSize: 12,
                       fontFamily: "Bebas Neue, sans-serif" }}>
          <Sparkles size={14} /> NEARNOW REFERRAL PROGRAM
        </div>
        <h3 style={{ margin: "6px 0 2px", color: NAVY, fontFamily: "Bebas Neue, sans-serif",
                      letterSpacing: 1, fontSize: 22 }}>
          REFER A CONTRACTOR · EARN <span style={{ color: GOLD }}>$50 + 1 FREE MONTH</span>
        </h3>
        <p style={{ margin: 0, fontSize: 13, color: GREY }}>
          Share your link, get $50 cash + your next month free for every paid signup. No cap.
        </p>
      </div>
      <Link
        data-testid="refer-earn-cta"
        to={`/contractor/${contractorId}/referrals`}
        className="btn-gold tap"
        style={{ minHeight: 52, fontSize: 15, paddingLeft: 22, paddingRight: 22,
                  letterSpacing: 1, textAlign: "center" }}
      >
        GET MY LINK →
      </Link>
    </div>
  </section>
);
